import React, { useState, useMemo, memo, useCallback, useEffect } from "react";
import useCanHover from "../hooks/useCanHover";
import { useNavigate, useLocation } from "react-router-dom";
// Removed framer-motion import for better performance - using CSS transitions instead
import { Heart, Star, Ruler, ImageOff, Truck } from "lucide-react";
// Removed unused useQuery import
import { useWishlist } from "../contexts/WishlistContext";
import { useAuth } from "../contexts/AuthContext";
import { addToCart as addToCartUnified } from "../services/cart/cartService";
import { formatCurrencyNPR } from "../utils/currency";
import { cleanProductTitle, getProductPrice } from "../utils/productUtils";
import { Product } from "../types";
import { isOutOfStock } from "../utils/stockUtils";
import toast from "react-hot-toast";
import MobileProductDetail from "./MobileProductDetail";
import ProductPreferencesModal from "./ProductPreferencesModal";
import ProductImageGallery from "./ProductImageGallery";
import SizeGuideModal from "./SizeGuideModal";
// Removed direct import - will use CartContext instead
import { useProductReviews } from "../hooks/useProductReviews";
import { useUserTracking } from "../hooks/useUserTracking";
import { useAuthPrompt } from "../hooks/useAuthPrompt";
import { AuthPromptModal } from "./auth/AuthPromptModal";
import LazyImage from "./common/LazyImage";
import { useFlashSaleAnalytics } from "../hooks/useFlashSaleAnalytics";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebaseConfig";
import FreeShippingBadge from "./FreeShippingBadge";
import { useVoucherBadge } from "../hooks/useVoucherBadge";

// Local minimal helpers to satisfy references
const validateNumber = (
  num: any,
  label: string,
  min: number = 1,
  max: number = 100
): { valid: boolean; message?: string } => {
  const n = Number(num);
  if (!Number.isFinite(n))
    return { valid: false, message: `${label} must be a number` };
  if (n < min)
    return { valid: false, message: `${label} must be at least ${min}` };
  if (n > max)
    return { valid: false, message: `${label} must be at most ${max}` };
  return { valid: true };
};

interface ProductCardProps {
  product: Product;
  className?: string;
  showAddToCart?: boolean;
  compact?: boolean;
  overridePrice?: number;
  overrideDiscountPct?: number;
  showVariationsBadges?: boolean;
  hideWishlistButton?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = memo(
  ({
    product,
    className = "",
    showAddToCart = true,
    compact = false,
    overridePrice,
    overrideDiscountPct,
    showVariationsBadges = false,
    hideWishlistButton = false,
  }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const canHover = useCanHover();
    const [showMobileDetail, setShowMobileDetail] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [showImageGallery, setShowImageGallery] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [addToCartLoading, setAddToCartLoading] = useState(false);
    const [showSizeGuide, setShowSizeGuide] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);

    // Ensure required fields exist with defaults for useMemo dependencies
    const {
      id = "",
      title = "Untitled Product",
      price = 0,
      discountPrice,
      images = [],
      hasFreeShipping = false,
    } = product || {};

    const { currentUser } = useAuth();
    const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
    const { stats, loading: ratingLoading } = useProductReviews(id);
    const { showAuthPrompt, modalProps: authModalProps } = useAuthPrompt();
    // Check vouchers (hook will optimize internally if product already has free shipping)
    const hasMatchingVoucher = useVoucherBadge(product);
    
    // Determine if badge should show - prioritize product.hasFreeShipping for immediate display
    const shouldShowFreeDeliveryBadge = product.hasFreeShipping || hasMatchingVoucher;
    const {
      trackProductClick,
      trackAddToCart,
      trackAddToWishlist,
    } = useUserTracking();
    const { trackClick: trackFlashSaleClick, trackAddToCart: trackFlashSaleAddToCart } = useFlashSaleAnalytics();
    const calculatedRating = stats?.averageRating || 0;
    
    // Check if product is in flash sale - ONLY use flash_sales collection logic (same as FlashSaleSection)
    const [isFlashSaleProduct, setIsFlashSaleProduct] = useState(false);
    const [flashSaleId, setFlashSaleId] = useState<string | null>(null);
    
    useEffect(() => {
      const checkFlashSale = async () => {
        if (!id) {
          setIsFlashSaleProduct(false);
          setFlashSaleId(null);
          return;
        }
        
        // ONLY check if product is in flash_sales collection (same logic as FlashSaleSection)
        try {
          const flashSalesCol = collection(db, 'flash_sales');
          const flashSaleQuery = query(
            flashSalesCol,
            where('status', '==', 'active'),
            where('productIds', 'array-contains', id)
          );
          const snapshot = await getDocs(flashSaleQuery);
          if (!snapshot.empty) {
            setIsFlashSaleProduct(true);
            setFlashSaleId(snapshot.docs[0].id);
          } else {
            setIsFlashSaleProduct(false);
            setFlashSaleId(null);
          }
        } catch (error) {
          // Silently fail - product is not in flash sale
          setIsFlashSaleProduct(false);
          setFlashSaleId(null);
        }
      };
      
      checkFlashSale();
    }, [id]);

    // Calculate effective price using centralized function
    const effectivePrice = useMemo(() => {
      if (overridePrice !== undefined) return overridePrice;
      return getProductPrice(product);
    }, [product, overridePrice]);

    // Calculate discount percentage
    const discountPercentage = useMemo(() => {
      if (overrideDiscountPct !== undefined) return overrideDiscountPct;
      if (!discountPrice || discountPrice >= price) return 0;
      return Math.round(((price - discountPrice) / price) * 100);
    }, [price, discountPrice, overrideDiscountPct]);


    // Check if product is in wishlist
    const isInWishlistState = useMemo(() => {
      return isInWishlist(id);
    }, [id, isInWishlist]);

    // Check if product is out of stock
    const outOfStock = useMemo(() => {
      return isOutOfStock(product);
    }, [product]);

    // Get stock status (reserved for future use)
    // const stockStatusText = useMemo(() => {
    //   return getStockStatusText(product);
    // }, [product]);

    // const stockStatusColor = useMemo(() => {
    //   return getStockStatusColor(product);
    // }, [product]);

    // Handle product click - redirect to product detail page
    const handleProductClick = useCallback(
      (e: React.MouseEvent) => {
        // Check if the click target is a button or inside a button
        const target = e.target as HTMLElement;
        const isButton = target.closest("button");
        const isQuickAction = target.closest("[data-quick-action]");

        // Track product click
        if (id) {
          trackProductClick(id, title);
          // Also track flash sale analytics if applicable
          if (isFlashSaleProduct && flashSaleId) {
            trackFlashSaleClick(flashSaleId, id, {
              source: 'product_card',
              pageUrl: location.pathname,
            }).catch(() => {
              // Silently fail analytics
            });
          }
        }

        // Only navigate if the click is not on a button or quick action
        if (!isButton && !isQuickAction && id) {
          navigate(`/product/${id}`, { state: { from: location.pathname } });
        }
      },
      [id, title, navigate, trackProductClick]
    );

    // Prefetch product data
    const prefetchProduct = useCallback(() => {
      // This would typically prefetch the product data
      // For now, we'll just navigate
    }, []);

    // Handle wishlist toggle
    const handleWishlistToggle = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!currentUser?.uid) {
          showAuthPrompt("wishlist", `/product/${id}`);
          return;
        }

        try {
          setWishlistLoading(true);
          if (isInWishlistState) {
            await removeFromWishlist(id);
            toast.success("Removed from wishlist");
          } else {
            await addToWishlist(id, {
              name: cleanProductTitle(title),
              image: images?.[0] || "",
              price: effectivePrice,
            });
            // Track wishlist addition
            trackAddToWishlist(id, title);
            toast.success("Added to wishlist");
          }
        } catch (error) {
          toast.error("Failed to update wishlist");
        } finally {
          setWishlistLoading(false);
        }
      },
      [
        currentUser,
        id,
        isInWishlistState,
        title,
        images,
        effectivePrice,
        addToWishlist,
        removeFromWishlist,
        showAuthPrompt,
        trackAddToWishlist,
      ]
    );

    // Handle add to cart
    const handleAddToCart = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!currentUser?.uid) {
          showAuthPrompt("add_to_cart", `/product/${id}`);
          return;
        }

        // For variable products, always show preferences modal (let modal handle stock validation)
        if (
          product.productType === "variable" &&
          (product.variants?.length || 0) > 0
        ) {
          setShowPreferencesModal(true);
          return;
        }

        // For simple products, check if out of stock
        if (outOfStock) {
          toast.error("This product is currently out of stock");
          return;
        }

        if (!currentUser?.uid) {
          navigate("/login");
          return;
        }

        try {
          // For simple products, use unified service for proper validation and transaction safety
          setAddToCartLoading(true);
          await addToCartUnified({
            userId: currentUser.uid,
            product: product,
            quantity: 1,
            selectedVariation: {
              size: "One Size",
              color: "Default",
            },
          });
          // Track add to cart
          trackAddToCart(id, title, effectivePrice);
          // Also track flash sale analytics if applicable
          if (isFlashSaleProduct && flashSaleId) {
            trackFlashSaleAddToCart(flashSaleId, id, {
              source: 'product_card',
              price: effectivePrice,
            }).catch(() => {
              // Silently fail analytics
            });
          }
          toast.success("Added to cart");
        } catch (error: any) {
          // SECURITY: Removed console.error to prevent information leakage
          const errorMessage = error?.message || "Failed to add to cart";
          toast.error(errorMessage);
        } finally {
          setAddToCartLoading(false);
        }
      },
      [
        currentUser,
        product,
        outOfStock,
        effectivePrice,
        id,
        title,
        navigate,
        showAuthPrompt,
        trackAddToCart,
      ]
    );

    // Handle preferences modal actions
    const handlePreferencesAddToCart = useCallback(
      async (
        quantity: number,
        selectedSize?: string,
        selectedColor?: string
      ) => {
        if (!currentUser?.uid) {
          showAuthPrompt("add_to_cart", `/product/${id}`);
          return;
        }

        try {
          // Security: Enhanced validate quantity
          const quantityValidation = validateNumber(
            quantity,
            "Quantity",
            1,
            100
          );
          if (!quantityValidation.valid) {
            // SECURITY: Removed console.warn to prevent information leakage
            toast.error("Invalid quantity");
            return;
          }

          setAddToCartLoading(true);

          // Use unified service for proper validation and transaction safety
          await addToCartUnified({
            userId: currentUser.uid,
            product: product,
            quantity,
            selectedVariation: {
              size: selectedSize,
              color: selectedColor,
            },
          });
          toast.success("Added to cart");
          setShowPreferencesModal(false);
        } catch (error: any) {
          // SECURITY: Removed console.error to prevent information leakage
          const errorMessage = error?.message || "Failed to add to cart";
          toast.error(errorMessage);
        } finally {
          setAddToCartLoading(false);
        }
      },
      [currentUser, product, showAuthPrompt, id, navigate]
    );

    const handlePreferencesBuyNow = useCallback(
      async (
        quantity: number,
        selectedSize?: string,
        selectedColor?: string
      ) => {
        if (!currentUser?.uid) {
          showAuthPrompt("buy_now", `/product/${id}`);
          return;
        }

        try {
          // Security: Enhanced validate quantity
          const quantityValidation = validateNumber(
            quantity,
            "Quantity",
            1,
            100
          );
          if (!quantityValidation.valid) {
            // SECURITY: Removed console.warn to prevent information leakage
            toast.error("Invalid quantity");
            return;
          }

          setAddToCartLoading(true);

          // Use unified service for proper validation and transaction safety
          await addToCartUnified({
            userId: currentUser.uid,
            product: product,
            quantity,
            selectedVariation: {
              size: selectedSize,
              color: selectedColor,
            },
            source: "buy_now",
          });
          toast.success("Added to cart");
          setShowPreferencesModal(false);
          // Navigate to checkout for buy now
          navigate("/checkout", { state: { buy_now: true } });
        } catch (error: any) {
          // SECURITY: Removed console.error to prevent information leakage
          const errorMessage =
            error?.message || "Failed to proceed to checkout";
          toast.error(errorMessage);
        } finally {
          setAddToCartLoading(false);
        }
      },
      [currentUser, product, showAuthPrompt, id, navigate]
    );

    // Wrapper function for MobileProductDetail
    const handleMobileWishlistToggle = useCallback(() => {
      handleWishlistToggle({ stopPropagation: () => {} } as React.MouseEvent);
    }, [handleWishlistToggle]);

    // Handle image sliding on hover
    React.useEffect(() => {
      if (!isHovering || !images || images.length <= 1) {
        return;
      }

      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 1000); // Change image every 1 second

      return () => clearInterval(interval);
    }, [isHovering, images]);

    // Reset to first image when not hovering
    React.useEffect(() => {
      if (!isHovering) {
        setCurrentImageIndex(0);
      }
    }, [isHovering]);

    // Validate product data to prevent rendering errors - moved after all hooks
    if (!product || typeof product !== "object") {
      // SECURITY: Removed console.error to prevent information leakage
      return null;
    }

    // Validate product ID
    if (!id) {
      // SECURITY: Removed console.error to prevent information leakage
      return null;
    }

    // Product data validated successfully - no need to log every validation

    const imageAspectClass = "aspect-square";
    // const containerMinHeight = compact
    //   ? "min-h-[220px]" // Further reduced for better desktop fit
    //   : "min-h-[260px]"; // Further reduced for better desktop fit

    return (
      <>
        <div
          className={`group relative rounded-lg overflow-hidden transition-all duration-200 w-full cursor-pointer focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 h-full flex flex-col ${
            product.isFeatured 
              ? 'bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-50 p-0.5 shadow-md shadow-yellow-200/50' 
              : 'bg-white border border-gray-200'
          } ${className}`}
          onMouseEnter={() => {
            if (canHover) {
              prefetchProduct();
              setIsHovering(true);
            }
          }}
          onMouseLeave={() => {
            setIsHovering(false);
          }}
          onClick={handleProductClick}
          style={{ cursor: "pointer", position: "relative" }}
          data-product-id={id}
          tabIndex={0}
          role="button"
          aria-label={`View ${cleanProductTitle(title)} - ${formatCurrencyNPR(
            effectivePrice
          )}`}
        >
          {/* Inner container for featured products */}
          <div className={`h-full flex flex-col ${product.isFeatured ? 'bg-white rounded-lg' : ''}`}>
          {/* Upper Section: Image Area (Major) */}
          <div
            className={`relative w-full ${imageAspectClass} overflow-hidden bg-white flex-shrink-0`}
          >
            {images && images.length > 0 ? (
              <div className="relative w-full h-full">
                {images.map((image, index) => (
                  <LazyImage
                    key={index}
                    src={image}
                    alt={cleanProductTitle(title) || "Product"}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      index === currentImageIndex ? "opacity-100" : "opacity-0"
                    }`}
                    srcSet={`${image} 320w, ${image} 480w, ${image} 640w, ${image} 960w`}
                    sizes="(min-width:1024px) 25vw, (min-width:768px) 33vw, (min-width:640px) 50vw, 100vw"
                    showPlaceholder={true}
                    priority={index === 0}
                    onError={() => {
                      if (index === 0) setImageError(true);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                <ImageOff
                  className={`${
                    compact ? "w-8 h-8" : "w-12 h-12 sm:w-16 sm:h-16"
                  } text-gray-400 mb-2`}
                />
                <span
                  className={`${
                    compact ? "text-[10px]" : "text-xs sm:text-sm"
                  } text-gray-500 font-medium`}
                >
                  No Image
                </span>
              </div>
            )}

            {/* Overlay: Free Shipping Badge - Top Left (above discount) */}
            {/* Show immediately if product.hasFreeShipping is true, or after voucher check completes */}
            {shouldShowFreeDeliveryBadge && (
              <div
                className={`absolute ${
                  compact ? "top-1 left-1" : "top-1 sm:top-2 left-1 sm:left-2"
                } z-10`}
              >
                <div
                  className={`inline-flex items-center gap-0.5 ${
                    compact
                      ? "px-1.5 py-0.5 text-[9px] sm:text-[10px]"
                      : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs"
                  } rounded-md bg-green-600 text-white font-poppins font-bold`}
                >
                  <Truck className={`${compact ? "w-2.5 h-2.5" : "w-3 h-3"} flex-shrink-0`} />
                  <span>FREE DELIVERY</span>
                </div>
              </div>
            )}

            {/* Overlay: Discount Badge - Top Left (below free shipping if both exist) */}
            {discountPercentage > 0 && (
              <div
                className={`absolute ${
                  compact 
                    ? shouldShowFreeDeliveryBadge ? "top-7 left-1" : "top-1 left-1"
                    : shouldShowFreeDeliveryBadge ? "top-8 sm:top-10 left-1 sm:left-2" : "top-1 sm:top-2 left-1 sm:left-2"
                } z-10`}
              >
                <div
                  className={`inline-flex items-center ${
                    compact
                      ? "px-1.5 py-0.5 text-[9px] sm:text-[10px]"
                      : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs"
                  } rounded-md bg-orange-500 text-white font-poppins font-bold`}
                >
                  -{discountPercentage}%
                </div>
              </div>
            )}

            {/* Overlay: Flash Sale Badge - Bottom Left (always show for flash sale products) */}
            {isFlashSaleProduct && (
              <div
                className={`absolute ${
                  compact ? "bottom-1 left-1" : "bottom-1 sm:bottom-2 left-1 sm:left-2"
                } z-10`}
              >
                <div
                  className={`inline-flex items-center gap-0.5 ${
                    compact
                      ? "px-1.5 py-0.5 text-[9px] sm:text-[10px]"
                      : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs"
                  } rounded-md bg-primary-500 text-white font-poppins font-bold shadow-lg`}
                >
                  <span>⚡</span>
                  <span>FLASH SALE</span>
                </div>
              </div>
            )}



            {/* Overlay: Featured Icon - Top Right Corner */}
            {product.isFeatured && (
              <div
                className={`absolute ${
                  compact ? "top-1 right-1" : "top-1 sm:top-2 right-1 sm:right-2"
                } z-20`}
              >
                <div
                  className={`inline-flex items-center justify-center ${
                    compact ? "w-6 h-6 sm:w-7 sm:h-7" : "w-7 h-7 sm:w-8 sm:h-8"
                  } rounded-full bg-gradient-to-br from-yellow-400 via-orange-400 to-yellow-500 text-white shadow-lg backdrop-blur-sm`}
                  title="Featured Product"
                >
                  <Star
                    className={`${
                      compact
                        ? "w-3 h-3 sm:w-3.5 sm:h-3.5"
                        : "w-3.5 h-3.5 sm:w-4 sm:h-4"
                    } fill-current`}
                  />
                </div>
              </div>
            )}

            {/* Overlay: Action Buttons - Top Right */}
            <div
              className={`absolute top-1 sm:top-2 right-1 sm:right-2 z-10 flex flex-col ${
                compact ? "gap-1 sm:gap-1.5" : "gap-1.5 sm:gap-2"
              } ${product.isFeatured ? (compact ? "mt-7 sm:mt-8" : "mt-8 sm:mt-10") : ""}`}
            >
              {/* Wishlist Heart Button */}
              {!hideWishlistButton && (
                <button
                  type="button"
                  onClick={handleWishlistToggle}
                  disabled={wishlistLoading}
                  aria-label={
                    isInWishlistState
                      ? "Remove from wishlist"
                      : "Add to wishlist"
                  }
                  className={`inline-flex items-center justify-center ${
                    compact ? "w-6 h-6 sm:w-7 sm:h-7" : "w-7 h-7 sm:w-8 sm:h-8"
                  } rounded-full backdrop-blur-md transition-all duration-200 hover:scale-110 active:scale-95 ${
                    isInWishlistState
                      ? "bg-red-500/95 text-white"
                      : "bg-white/90 text-gray-600 hover:text-red-500 hover:bg-white"
                  }`}
                >
                  <Heart
                    className={`${
                      compact
                        ? "w-3 h-3 sm:w-3.5 sm:h-3.5"
                        : "w-3.5 h-3.5 sm:w-4 sm:h-4"
                    } transition-all duration-200 ${
                      isInWishlistState ? "fill-current" : ""
                    }`}
                  />
                </button>
              )}

              {/* Size Guide Button - Hidden to match mobile app minimalism */}
              {false && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSizeGuide(true);
                  }}
                  aria-label="Size guide"
                  className={`inline-flex items-center justify-center ${
                    compact ? "w-6 h-6 sm:w-7 sm:h-7" : "w-7 h-7 sm:w-8 sm:h-8"
                  } rounded-full bg-white/90 backdrop-blur-md text-gray-600 hover:text-blue-600 hover:bg-white transition-all duration-200 hover:scale-110 active:scale-95`}
                >
                  <Ruler
                    className={`${
                      compact
                        ? "w-3 h-3 sm:w-3.5 sm:h-3.5"
                        : "w-3.5 h-3.5 sm:w-4 sm:h-4"
                    }`}
                  />
                </button>
              )}
            </div>

            {/* Overlay: Out of Stock - Full Cover */}
            {outOfStock && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                <div className="text-white text-base sm:text-lg font-poppins font-bold tracking-wide">
                  Out of Stock
                </div>
              </div>
            )}
          </div>


          {/* Lower Section: All Info (Flexible Height) */}
          <div
            className={`flex-1 flex flex-col justify-between ${
              compact ? "p-1.5 sm:p-2" : "p-2 sm:p-3"
            } bg-white`}
          >
            {/* Product Info - Takes available space */}
            <div className={`flex-1 ${compact ? "space-y-1.5" : "space-y-2"}`}>
              {/* Compact Title with Better Space Usage */}
              <h3
                className={`font-poppins font-medium text-slate-900 ${
                  compact ? "text-sm sm:text-xs" : "text-sm sm:text-base"
                } leading-tight line-clamp-2 hover:text-red-600 transition-colors duration-200`}
              >
                {cleanProductTitle(title)}
              </h3>

              {/* Rating Display */}
              <div
                className={`flex items-center gap-1 ${
                  compact ? "text-[10px] sm:text-xs" : "text-xs sm:text-sm"
                } text-slate-600`}
              >
                <Star
                  className={`${
                    compact ? "w-2.5 h-2.5" : "w-3 h-3"
                  } text-amber-500 fill-current`}
                />
                <span className="font-poppins font-medium">
                  {ratingLoading
                    ? "..."
                    : calculatedRating?.toFixed(1) || "0.0"}
                </span>
              </div>

              {/* Price Display */}
              <div className="flex items-baseline gap-1">
                <span
                  className={`font-poppins font-bold text-red-600 ${
                    compact ? "text-xs sm:text-sm" : "text-sm sm:text-base"
                  }`}
                >
                  {formatCurrencyNPR(effectivePrice)}
                </span>
                {discountPrice && discountPrice < price && (
                  <span
                    className={`font-poppins text-slate-400 line-through ${
                      compact
                        ? "text-[10px] sm:text-xs"
                        : "text-xs sm:text-sm"
                    }`}
                  >
                    {formatCurrencyNPR(price)}
                  </span>
                )}
              </div>

            </div>

            {/* Add to Cart Button - Removed to match mobile app design */}
          </div>
          </div>
        </div>

        {/* Product Image Gallery Modal */}
        <ProductImageGallery
          product={product}
          isOpen={showImageGallery}
          onClose={() => setShowImageGallery(false)}
          initialImageIndex={0}
        />

        {/* Mobile Product Detail Modal */}
        <MobileProductDetail
          product={product}
          isOpen={showMobileDetail}
          onClose={() => setShowMobileDetail(false)}
          onAddToCart={() => navigate(`/product/${id}`, { state: { from: location.pathname } })}
          onBuyNow={() => navigate(`/product/${id}`, { state: { from: location.pathname } })}
          onWishlistToggle={handleMobileWishlistToggle}
          isInWishlist={isInWishlistState}
        />

        {/* Product Preferences Modal for Variable Products */}
        <ProductPreferencesModal
          product={product}
          isOpen={showPreferencesModal}
          onClose={() => setShowPreferencesModal(false)}
          onAddToCart={handlePreferencesAddToCart}
          onBuyNow={handlePreferencesBuyNow}
        />

        {/* Size Guide Modal */}
        <SizeGuideModal
          isOpen={showSizeGuide}
          onClose={() => setShowSizeGuide(false)}
          productCategory={product.categories?.[0] || "clothing"}
        />

        {/* Auth Prompt Modal */}
        <AuthPromptModal {...authModalProps} />
      </>
    );
  }
);

ProductCard.displayName = "ProductCard";

export default ProductCard;
